import './index.css';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useContext, useRef, useEffect, useLayoutEffect, useImperativeHandle, Fragment as Fragment$1 } from 'react';
import classnames from 'classnames';
import { CustomScrollContext } from '@hh.ru/magritte-internal-custom-scroll';
import { Divider } from '@hh.ru/magritte-ui-divider';
import { Layer } from '@hh.ru/magritte-ui-layer';
import { s as styles, N as NavigationBarItem } from './NavigationBarItem-DNi29tNN.js';
import { NavigationBarItems } from './NavigationBarItems.js';
import { initAnimations, QA, applyAnimations } from './animationUtils.js';
import { updateScrollGroundPos } from './updateScrollGroundPos.js';
import { ProgressBar } from '@hh.ru/magritte-ui-progress-bar';
import '@hh.ru/magritte-ui-action';
import '@hh.ru/magritte-ui-link';
import '@hh.ru/magritte-ui-icon/icon';
import './types.js';

const findScrollContainer = (element) => {
    if (!element) {
        return null;
    }
    let parentElement = element.parentElement;
    while (parentElement !== null) {
        const { overflowY } = window.getComputedStyle(parentElement);
        if (overflowY.includes('auto') || overflowY.includes('scroll')) {
            return parentElement;
        }
        parentElement = parentElement.parentElement;
    }
    return null;
};
const renderStringNode = (node, beforeScroll) => {
    return typeof node === 'function' ? node(beforeScroll) : node;
};
const StandardTitle = forwardRef(({ left, right, ellipsisRenderFunc, 'data-qa': dataQa, children }, ref) => {
    return (jsxs("div", { className: styles.spread, "data-qa": dataQa, role: "menu", ref: ref, children: [jsx("div", { className: styles.left, children: left && jsx(NavigationBarItem, { role: "menuitem", item: left }) }), jsxs("div", { className: styles.right, children: [jsx("div", { className: styles.halfTitleWrapper, children: children }), jsx("div", { className: styles.rightDummy, children: jsx(NavigationBarItems, { items: right, size: "standard", renderFunc: ellipsisRenderFunc }) })] })] }));
});
StandardTitle.displayName = 'StandardTitle';
const NavigationBarLayer = ({ children }) => {
    return jsx(Layer, { layer: "navigation-bar", children: children });
};
const NavigationBarComponent = ({ size = 'standard', title, subtitle, left, progress, right, options, allowOptionsScroll = false, showDivider = false, ellipsisRenderFunc, inLayer, forwardedRef, transformTriggerPosition = 'start', }) => {
    const scrollContext = useContext(CustomScrollContext);
    const stateRef = useRef({
        size,
        heightObservers: [],
        transitionProgress: 0,
        animationProgress: 0,
        skipFirstResizeCallback: true,
        scrollGroundPos: 0,
        currentScrollTop: 0,
        maxScrollTop: 0,
        allowOptionsScroll,
        transformTriggerPosition,
        containerObserver: null,
        sizes: {
            itemsContainer: 0,
            largeTitle: 0,
            standardTitle: 0,
            optionsContainer: 0,
        },
        animations: [],
    });
    stateRef.current.allowOptionsScroll = allowOptionsScroll;
    stateRef.current.size = size;
    stateRef.current.transformTriggerPosition = transformTriggerPosition;
    const itemsContainerRef = useRef(null);
    const titleBeforeScrollRef = useRef(null);
    const optionsContainerRef = useRef(null);
    const dividerRef = useRef(null);
    const titleAfterScrollRef = useRef(null);
    const standardItemsRef = useRef(null);
    const largeItemsRef = useRef(null);
    const wrapperRef = useRef(null);
    const backgroundContainerRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const scrollContainerRef = useRef();
    useEffect(() => {
        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                if (stateRef.current.skipFirstResizeCallback) {
                    stateRef.current.skipFirstResizeCallback = false;
                    return;
                }
                stateRef.current.heightObservers.forEach((observer) => observer());
            });
        }
        const resizeObserver = resizeObserverRef.current;
        const titleContainer = titleBeforeScrollRef.current;
        const optionsContainer = optionsContainerRef.current;
        titleContainer && resizeObserver.observe(titleContainer);
        optionsContainer && resizeObserver.observe(optionsContainer);
        return () => {
            titleContainer && resizeObserver.unobserve(titleContainer);
            optionsContainer && resizeObserver.unobserve(optionsContainer);
        };
    }, [size]);
    useLayoutEffect(() => {
        stateRef.current.containerObserver?.disconnect();
        stateRef.current.containerObserver = null;
        stateRef.current.scrollGroundPos = 0;
        const sizes = stateRef.current.sizes;
        sizes.standardTitle = titleAfterScrollRef.current?.clientHeight ?? 0;
        sizes.optionsContainer = optionsContainerRef.current?.clientHeight ?? 0;
        sizes.itemsContainer = itemsContainerRef.current?.clientHeight ?? 0;
        sizes.largeTitle = size === 'large' ? (titleBeforeScrollRef.current?.clientHeight ?? 0) : 0;
        initAnimations(stateRef.current, backgroundContainerRef, titleBeforeScrollRef, titleAfterScrollRef, optionsContainerRef, largeItemsRef, standardItemsRef, dividerRef);
        const handleScroll = (scrollTop, forceRunCallbacks = false) => {
            updateScrollGroundPos(stateRef.current, scrollTop, scrollContainerRef, wrapperRef);
            applyAnimations(stateRef.current, forceRunCallbacks);
            if (dividerRef.current &&
                (showDivider === 'with-scroll' || (size === 'large' && showDivider !== 'always'))) {
                dividerRef.current.classList.toggle(styles.invisibleElement, stateRef.current.transitionProgress >= scrollTop);
            }
        };
        stateRef.current.handleScroll = handleScroll;
        if (scrollContext !== null) {
            const handleCustomScroll = (event) => {
                stateRef.current.maxScrollTop = event.maxScrollTop;
                handleScroll(event.scrollTop);
            };
            scrollContainerRef.current = scrollContext.wrapperRef.current;
            scrollContext.subscribe(handleCustomScroll);
            return () => scrollContext.unsubscribe(handleCustomScroll);
        }
        scrollContainerRef.current = findScrollContainer(wrapperRef.current);
        let getScrollTop = () => 0;
        if (scrollContainerRef.current !== null) {
            const scrollContainer = scrollContainerRef.current;
            stateRef.current.maxScrollTop = scrollContainer.scrollHeight - scrollContainer.offsetHeight;
            getScrollTop = () => scrollContainer.scrollTop;
        }
        else {
            stateRef.current.maxScrollTop =
                document.documentElement.scrollHeight - visualViewport.height;
            getScrollTop = () => visualViewport.pageTop;
        }
        stateRef.current.currentScrollTop = getScrollTop();
        const handleNativeScroll = () => handleScroll(getScrollTop());
        const scrollContainer = scrollContainerRef.current || window;
        scrollContainerRef.current = scrollContainerRef.current ?? document.documentElement;
        scrollContainer.addEventListener('scroll', handleNativeScroll);
        return () => scrollContainer.removeEventListener('scroll', handleNativeScroll);
    }, [size, allowOptionsScroll, showDivider, scrollContext, options, transformTriggerPosition]);
    // вызываем обработку скролла на каждый ререндер чтобы применились анимации, изменения data-qa и pointer-events в
    // соответствии с фактическим состоянием NavigationBar
    useLayoutEffect(() => {
        stateRef.current.handleScroll?.(stateRef.current.currentScrollTop, true);
    });
    useImperativeHandle(forwardedRef, () => {
        return {
            /**
             * Фактическая высота, которую компонент занимает в родительском контейнере
             * Может отличаться от видимой высоты
             */
            get layoutHeight() {
                return wrapperRef.current?.clientHeight || 0;
            },
            /**
             * Высота видимой части компонента
             * Может отличаться от фактической высоты
             */
            get visibleHeight() {
                return this.layoutHeight - stateRef.current.transitionProgress;
            },
            addHeightObserver: (observer) => {
                stateRef.current.heightObservers.push(observer);
            },
            removeHeightObserver: (observer) => {
                stateRef.current.heightObservers = stateRef.current.heightObservers.filter((_observer) => _observer !== observer);
            },
        };
    }, []);
    const Wrapper = inLayer ? NavigationBarLayer : Fragment$1;
    const titleProps = { title, subtitle, left, progress, right, ellipsisRenderFunc };
    const titleLarge = title && renderStringNode(title, true);
    const subtitleLarge = subtitle && renderStringNode(subtitle, true);
    const titleStandard = title && renderStringNode(title, false);
    const subtitleStandard = subtitle && renderStringNode(subtitle, false);
    return (jsx(Wrapper, { children: jsx("div", { ref: wrapperRef, className: styles.navigationBarWrapper, children: jsxs("div", { className: styles.overflowContainer, children: [jsx("div", { className: styles.backgroundContainer, ref: backgroundContainerRef }), size === 'large' && (jsx("div", { className: styles.fixedItemsContainer, ref: itemsContainerRef, children: jsx("div", { className: styles.topPaddingOverride, children: jsxs("div", { className: classnames(styles.spread, styles.pointerEvents), "data-qa": QA.items, ref: largeItemsRef, role: "menu", children: [jsx("div", { className: styles.left, children: left && jsx(NavigationBarItem, { role: "menuitem", item: left }) }), jsxs("div", { className: styles.right, children: [progress && (jsx("div", { className: classnames(styles.progress), children: jsx("div", { className: styles.availableWidthWrapper, children: jsx(ProgressBar, { ...progress }) }) })), jsx(NavigationBarItems, { items: right, size: "large", renderFunc: ellipsisRenderFunc })] })] }) }) })), jsx("div", { className: classnames({
                            [styles.fixedTitleContainer]: size === 'large',
                            [styles.stickyTitleContainer]: size === 'standard',
                            [styles.pointerEvents]: size === 'standard',
                            [styles.invisibleElement]: size === 'large',
                        }), ref: size === 'large' ? titleAfterScrollRef : null, "data-qa": size === 'standard' ? QA.standardTitle : '', children: jsx("div", { className: styles.topPaddingOverride, children: jsx("div", { className: styles.standardTitleContainer, children: jsxs(StandardTitle, { ...titleProps, ref: standardItemsRef, "data-qa": size === 'standard' ? QA.items : '', children: [!!titleStandard && (jsxs("div", { className: styles.titleWrapper, ref: size === 'standard' ? titleAfterScrollRef : null, children: [jsx("div", { className: classnames(styles.title, {
                                                        [styles.titleWithoutSubtitle]: !subtitleStandard,
                                                    }), children: titleStandard }), !!subtitleStandard && (jsx("div", { className: styles.subtitle, children: subtitleStandard }))] })), size === 'standard' && !!titleLarge && (jsxs("div", { className: styles.titleWrapper, ref: titleBeforeScrollRef, children: [jsx("div", { className: classnames(styles.title, {
                                                        [styles.titleWithoutSubtitle]: !subtitleLarge,
                                                    }), children: titleLarge }), !!subtitleLarge && jsx("div", { className: styles.subtitle, children: subtitleLarge })] }))] }) }) }) }), size === 'large' && (jsx("div", { className: classnames(styles.largeTitleContainer, styles.pointerEvents), "data-qa": QA.largeTitle, ref: titleBeforeScrollRef, children: !!titleLarge && (jsxs(Fragment, { children: [jsx("div", { className: styles.title, children: titleLarge }), !!subtitleLarge && jsx("div", { className: styles.subtitle, children: subtitleLarge })] })) })), options && (jsx("div", { className: classnames({
                            [styles.overflowContainer]: allowOptionsScroll,
                        }), children: jsx("div", { className: classnames(styles.pointerEvents, styles.options), "data-qa": "navigation-bar-options", ref: optionsContainerRef, children: options }) })), (!!showDivider || size === 'large') && (jsx("div", { ref: dividerRef, className: classnames(styles.dividerContainer, {
                            [styles.invisibleElement]: showDivider !== 'always',
                        }), "data-qa": "navigation-bar-divider", children: jsx(Divider, {}) }))] }) }) }));
};

export { NavigationBarComponent };
//# sourceMappingURL=NavigationBarComponent.js.map
